import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';

// Define the structure of a video note
type IVideoNote = {
  videoHashId: string;
  data: any;
};

// Define the structure of a channel note, which contains multiple video notes
type IChannelNote = {
  channelHashId: string;
  videoNotes: Record<string, IVideoNote>;
};

// Define the overall state structure for the slice
export type channelNoteViewState = {
  channels: Record<string, IChannelNote>;
};

// Initial state
const initialState: channelNoteViewState = {
  channels: {}
};

// The slice itself
const ChannelNoteSlice = createSlice({
  name: 'channel-notes',
  initialState: initialState,
  reducers: {
    // Reducer to add or update a note for a specific video within a channel
    handleAddNote(state, action: PayloadAction<{ channelHashId: string; videoHashId: string; data: any }>) {
      const { channelHashId, videoHashId, data } = action.payload;
      // Ensure the channel exists, if not, create it
      if (!state.channels[channelHashId]) {
        state.channels[channelHashId] = {
          channelHashId,
          videoNotes: {}
        };
      }
      state.channels[channelHashId].videoNotes[videoHashId] = {
        videoHashId,
        data
      };
    },
    insertNewNote(state, action: PayloadAction<{ channelHashId: string; videoHashId: string; newNote: any }>) {
      const { channelHashId, videoHashId, newNote } = action.payload;
      if (!state.channels[channelHashId]) {
        state.channels[channelHashId] = {
          channelHashId,
          videoNotes: {}
        };
      }
      // Insert the new note into the videoNotes of the specific channel
      if (state.channels[channelHashId].videoNotes[videoHashId]) {
        state.channels[channelHashId].videoNotes[videoHashId].data = state.channels[channelHashId].videoNotes[
          videoHashId
        ].data.map((videoSlot: any) => {
          if (videoSlot.videoshot_hash_id === newNote.shot_hash_id) {
            videoSlot.notes.push(newNote);
            videoSlot.notes.sort((a: { time: number }, b: { time: number }) => a.time - b.time);
          }
          return videoSlot;
        });
      }
    },
    editNote(
      state,
      action: PayloadAction<{
        channelHashId: string;
        videoHashId: string;
        shotHashId: string;
        noteHashId: string;
        updatedContent: Partial<any>; // Adjust the type if you have a defined Note interface
      }>
    ) {
      const { channelHashId, videoHashId, shotHashId, noteHashId, updatedContent } = action.payload;

      const channel = state.channels[channelHashId];
      if (!channel) return;

      const videoNote = channel.videoNotes[videoHashId];
      if (!videoNote) return;

      const videoSlot = videoNote.data.find((slot: any) => slot.videoshot_hash_id === shotHashId);
      if (!videoSlot) return;

      const noteIndex = videoSlot.notes.findIndex((note: any) => note.hash_id === noteHashId);
      if (noteIndex === -1) return;

      videoSlot.notes[noteIndex] = {
        ...videoSlot.notes[noteIndex],
        ...updatedContent
      };
    },
    deleteNote(
      state,
      action: PayloadAction<{
        channelHashId: string;
        videoHashId: string;
        shotHashId: string;
        noteHashId: string;
      }>
    ) {
      const { channelHashId, videoHashId, shotHashId, noteHashId } = action.payload;

      const channel = state.channels[channelHashId];
      if (!channel) return;

      const videoNote = channel.videoNotes[videoHashId];
      if (!videoNote) return;

      const videoSlot = videoNote.data.find((slot: any) => slot.videoshot_hash_id === shotHashId);
      if (!videoSlot) return;

      videoSlot.notes = videoSlot.notes.filter((note: any) => note.hash_id !== noteHashId);
    }
  }
});

export const { handleAddNote, insertNewNote, editNote, deleteNote } = ChannelNoteSlice.actions;
export const channelNoteReducers = ChannelNoteSlice.reducer;

// Selector to get the notes of a specific channel and video
export const selectNoteByChannelAndVideo = createSelector(
  (state: { channelNotes: channelNoteViewState }, channelHashId: string, videoHashId: string) => ({
    channel: state.channelNotes.channels[channelHashId],
    videoHashId
  }),
  ({ channel, videoHashId }) => {
    if (!channel) {
      return null; // Channel doesn't exist
    }
    const videoNote = channel.videoNotes[videoHashId]?.data;
    return videoNote || []; // Return the video note or null if it doesn't exist
  }
);
